<template>
  <Layout>
    <div class="mc-list-header">
      <PageHeader :title="title">
        <template #afterTitle>
          <div class="d-flex align-items-center gap-2">
            <McButtonBack href="/report" :query="{ domain: domain }" />
            <ShareReport
              :report="report"
              :filter="baseFilter"
              @doShare="downloadReport"
              :dropdownItems="itemDownloadDropdown"
              :labelGraph="getActiveDataSet"
              :allColumn="allColumn"
            />
            <DownloadReportDropdown
              :report="report"
              :items="itemDownloadDropdown"
              :actualTotal="actualTotal"
            />
          </div>
        </template>
      </PageHeader>
    </div>
    <div class="d-flex align-items-center gap-2 me-2 absolute-right">
      <FavoriteReportButton
        v-if="hasAccess(['favorite-reports:update']) && !report.isSaved"
        :report="report"
      />
      <ModalSavedReport
        v-if="hasAccess(['saved-reports:read']) && !report.isSaved"
        :filter="filterValue"
        :detail="filterDetail"
        :report="report"
      />
      <McButton
        variant="secondary"
        :text="$t('report.title.reportParameter')"
        icon="icon-outline_filter"
        @click="showModal"
      />
    </div>
    <b-card :key="summaryKey" class="mb-2 mc-general__label" style="margin-top: 80px">
      <h5 class="mc-general__label">{{ $t('report.fields.dataSummary') }}</h5>
      <b-row>
        <b-col class="pt-2" sm="14" md="2">
          <b-form-group
            id="periodReport_label"
            :label="$t('report.object.reportPeriod')"
            label-for="reportPeriod"
          >
            <span v-if="!isBusy">
              {{ periodeLabel }}
            </span>
            <b-skeleton width="70%" v-else />
          </b-form-group>
        </b-col>
        <b-col class="pt-2" sm="14" md="2">
          <b-form-group
            id="licensePlate_label"
            :label="$t('report.fields.licensePlate')"
            label-for="licensePlate"
          >
            <div v-if="!isBusy" class="d-flex align-items-center gap-2">
              <span>{{ licensePlateLabel }}</span>
              <McBadge
                v-if="vehicleIdCount > 2"
                :text="`+${vehicleIdCount - 2}`"
                variant="success"
                size="small"
                type="pill"
              />
            </div>
            <b-skeleton width="70%" v-else />
          </b-form-group>
        </b-col>
      </b-row>
    </b-card>
    <AlertReportTooMany v-if="actualTotal > totalRows" />
    <McGrapqlTable
      :key="tableKey"
      bordered
      withCard
      :searchable="false"
      :fields="fields"
      :apiClient="apiClient"
      :hasCheckbox="false"
      :lazyLoadAddress="columns.includes('address')"
      reportKey="temperature"
      :hasGraph="isShowGraph ?? true"
      @handleTableChanged="handleTableChanged"
      v-if="Object.keys(filters).length > 0"
    >
      <template #viewManager="{ handleSetDataFields, dataColumns, initialFields }">
        <McFilterSelect
          :defaultColumnOrder="initialFields"
          :currentColumnOrder="dataColumns"
          @setColumn="handleSetDataFields"
          @setColumnReport="handleColumnReport"
        />
      </template>
      <template #graphic="{ dataGraph }">
        <b-skeleton-wrapper :loading="dataGraph.isLoadingGraph ?? true">
          <template #loading>
            <b-skeleton-img no-aspect height="400px" />
          </template>
          <LineReport
            :v-if="!dataGraph.isLoadingGraph"
            :labels="dataGraph.chartData?.labels ?? []"
            :datasets="dataGraph.chartData?.datasets ?? []"
            y-label="Celcius"
            :y-axes-additional="dataGraph.yAxesAdditional ?? yAxesAdditional"
          />
        </b-skeleton-wrapper>
      </template>
      <template #cell(createdOn)="data">
        {{ momentDateShortMonth(data.item.createdOn) }}<br />
        <span class="text-muted">{{ momentTimeSeconds(data.item.createdOn, true) }}</span>
      </template>
      <template #cell(driver)="data">
        {{ data.item.driver ? data.item.driver.fullname : '-' }}
      </template>
      <template #cell(vehicle)="data">
        {{ data.item.vehicle ? data.item.vehicle.licensePlate : '-' }}
      </template>

      <template #cell(vehicleType)="data">
        {{
          data.item.vehicle
            ? data.item.vehicle.vehicleType
              ? data.item.vehicle.vehicleType.manufacturer
              : '-'
            : '-'
        }}
        /
        {{
          data.item.vehicle
            ? data.item.vehicle.vehicleType
              ? data.item.vehicle.vehicleType.model
              : '-'
            : '-'
        }}
        /
        {{
          data.item.vehicle
            ? data.item.vehicle.vehicleType
              ? data.item.vehicle.vehicleType.type
              : '-'
            : '-'
        }}
      </template>

      <template #cell(temperature)="data">
        {{ data.item.temperature === null ? '-' : data.item.temperature }}
      </template>

      <template #cell(temperature2)="data">
        {{ data.item.temperature2 === null ? '-' : data.item.temperature2 }}
      </template>

      <template #cell(temperature3)="data">
        {{ data.item.temperature3 === null ? '-' : data.item.temperature3 }}
      </template>

      <template #cell(acc)="data">
        {{
          data.item.acc
            ? data.item.acc == 'on'
              ? $t('report.label.on')
              : $t('report.label.off')
            : '-'
        }}
      </template>

      <template #cell(refrigerator)="data">
        {{
          data.item.refrigerator
            ? data.item.refrigerator == 'on'
              ? $t('report.label.on')
              : $t('report.label.off')
            : '-'
        }}
      </template>

      <template #cell(door1)="data">
        <span v-if="data.item.door1 !== null">{{
          data.item.door1 == 0 ? $t('global.alert.close') : $t('global.alert.open')
        }}</span>
        <span v-else>-</span>
      </template>

      <template #cell(door2)="data">
        <span v-if="data.item.door2 !== null">{{
          data.item.door2 == 0 ? $t('global.alert.close') : $t('global.alert.open')
        }}</span>
        <span v-else>-</span>
      </template>

      <template #cell(address)="data">
        <b-skeleton v-if="data.item.address == ''" width="70%" />
        <AddressValue
          v-else
          :id="`address-${data.index}`"
          :latitude="data.item.latitude"
          :longitude="data.item.longitude"
          :address="data.item.address"
        />
      </template>
    </McGrapqlTable>
    <ModalFilter
      v-if="isMounted"
      ref="modal-filter"
      :report="report"
      :unRedirect="true"
      @refresh-table="forceRerender"
    />
    <McReportDownloader />
    <ModalSavedReportVerification
      :key="modalVerifReport"
      :domain="domain"
      :message="message"
      :error="isError"
    />
    <ModalEmail
      v-if="actualTotal > totalRows"
      :id="idEmailModal"
      :recipients="recipientsEmail"
      @submit-emails="handleEmails"
    />
  </Layout>
</template>
<script>
import McButton from '@/components/McButton'
import appConfig from '@/app.config'
import PageHeader from '@/components/page-header-v2'
import Layout from '@/router/layouts/main'
import McButtonBack from '@/components/McButtonBack'
import McGrapqlTable from '@/components/McGrapqlTable'
import McFilterSelect from '@/components/McFilterSelect'
import McReportDownloader from '@/components/McReportDownloader'
import { Helper } from '@/mixin/helper'
import moment from 'moment'
import fields from './lib/fields/fields'
import {
  GET_REPORT_RAW_SENSOR,
  GET_REPORT_RAW_SENSOR_PAGINATED,
  RAW_SENSOR_INFO_REPORT,
} from '@/services/api/report/report'
import ModalFilter from '@/router/views/report/components/ModalFilter'
import reportList from '../../lib/reportList.js'
import { MixPanel } from '@/mixin/mixpanel'
import LineReport from '@/components/Chart/LineReport.vue'
import ModalSavedReport from '@/router/views/report/components/ModalSavedReport'
import { mapGetters } from 'vuex'
import { QueryCache } from '@/mixin/query-cache'
import responsive from '@/mixin/responsive'
import ModalSavedReportVerification from '@/router/views/report/components/ModalSavedReportVerification'
import FavoriteReportButton from '@/router/views/report/components/FavoriteReportButton'
import DownloadReportDropdown from '@/router/views/report/components/DownloadReportDropdown'
import ModalEmail from '@/router/views/report/components/ModalEmail'
import McBadge from '@/components/McBadge'
import AlertReportTooMany from '../../components/AlertReportTooMany.vue'
import ShareReport from '../../components/ShareReport.vue'
import AddressValue from '../../components/AddressValue.vue'
moment.locale('id')

export default {
  page: {
    title: 'Report',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
    McButtonBack,
    McGrapqlTable,
    McFilterSelect,
    McReportDownloader,
    McButton,
    ModalFilter,
    LineReport,
    ModalSavedReport,
    ModalSavedReportVerification,
    FavoriteReportButton,
    DownloadReportDropdown,
    ModalEmail,
    McBadge,
    AlertReportTooMany,
    ShareReport,
    AddressValue,
  },
  mixins: [Helper, MixPanel, responsive],
  data() {
    return {
      isMounted: false,
      series: [
        {
          name: 'Fuel (Liter)',
          data: [84, 94, 104, 71],
        },
        {
          name: 'Fuel (%)',
          data: [56, 66, 76, 43],
        },
        {
          name: 'Speed (Km/h)',
          data: [38, 48, 58, 26],
        },
        {
          name: 'ACC',
          data: [31, 41, 51, 19],
        },
        {
          name: 'Refrigerator',
          data: [21, 31, 41, 9],
        },
      ],
      chartData: {
        labels: [],
        datasets: [],
      },
      tableData: [],
      excelIcon: require('@/assets/images/download-format/excel.png'),
      pdfIcon: require('@/assets/images/download-format/pdf.png'),
      totalRows: 1,
      filters: {},
      tableKey: 0,
      isBusy: false,
      reportFields: fields.map((item, i) => ({ ...item, order: i + 1 })),
      reportKey: 'temperature',
      filterDetail: {},
      summaryKey: 50,
      yAxesAdditional: {
        id: 'pintu',
        ticks: {
          min: -1,
          max: 8,
          stepSize: 0.5,
          callback: function (label) {
            if (label == 7) {
              return 'Terbuka'
            } else if (label == 6) {
              return 'Tertutup'
            } else if (label == 3) {
              return 'Nyala'
            } else if (label == 2) {
              return 'Mati'
            } else if (label == 5) {
              return 'Nyala'
            } else if (label == 4) {
              return 'Mati'
            }
          },
        },
        gridLines: {
          color: 'rgba(166, 176, 207, 0.1)',
        },
        position: 'right',
        scaleLabel: {
          display: true,
          labelString: '  Status Pintu   Refrigerator    ACC                            ',
        },
      },
      isLoadingGraph: false,
      queryCache: new QueryCache(),
      message: '',
      isError: false,
      modalVerifReport: 100,
      emails: [],
      recipientsEmail: [],
      isSubmitEmail: false,
      idEmailModal: 'modal-email-temperature',
      reportAfterEmail: '',
      actualTotal: 0,
      downloadProgress: null,
      intervalDownload: null,
    }
  },
  mounted() {
    const emailStorage = localStorage.getItem('emailReceiverReport')
    if (emailStorage) {
      this.recipientsEmail = emailStorage.split(',')
    }

    this.reportKey =
      this.$route.query.key && this.savedReport.find((item) => item.key === this.$route.query.key)
        ? this.$route.query.key
        : this.reportKey
    const { reportFilter, reportFilterValue } = this.getFilterReport(this.reportKey)
    this.$store.commit('report/updateFilter', reportFilter)
    this.$store.commit('report/updateFilterValue', reportFilterValue)
    this.filters = { ...this.$store.state.report.filterReport }
    this.filterDetail = this.getReportDetail(this.reportKey) ?? {}
    this.isMounted = true
    this.track('View ' + this.report.event)
    if (!this.mediaQuery.matches) {
      this.$store.dispatch('layout/toggleMenu', true)
    }
  },
  computed: {
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
      getFeatureFlagData: 'identity/getFeatureFlagData',
    }),
    domain() {
      return this.report.domain
    },
    title() {
      return this.report.labelName ?? this.$t(this.report.label)
    },
    fields() {
      return this.reportFields
    },
    columns() {
      return this.reportFields
        .filter((item) => item.shown || item.shown === undefined)
        .map((field) => field.header)
    },
    allColumn() {
      return this.reportFields.map((field) => field.header)
    },
    baseFilter() {
      const filter = this.getBaseFilter(this.filters)
      const result = {
        ...filter,
        column: this.columns,
      }
      if (
        this.getFeatureFlagData('vsms2_report_temperature_version') &&
        this.checkApiVersion(result, this.report.reportKey ?? this.report.key)
      ) {
        result.version = this.getFeatureFlagData('vsms2_report_temperature_version').value
      }
      return result
    },
    generateFilter() {
      const { valid, msg } = this.validateFilter(this.report.filter, this.filters)
      if (valid) {
        const model = this.baseFilter
        return { valid: true, model }
      } else {
        this.$mcToast.error(msg)
        return { valid: false, model: {} }
      }
    },
    filterReport() {
      const { valid, model } = this.generateFilter
      if (!valid) return null
      const filterBody = {
        ...model,
      }
      return filterBody
    },
    isShowGraph() {
      return (
        this.getTotalDayReport(this.filters.periode) <= 7 &&
        this.hasOnlyOneVehicleReport(this.filters.vehicleId)
      )
    },
    apiClient() {
      return {
        get: async (filter) => {
          const model = this.filterReport
          if (!model) return {}
          const self = this
          self.isBusy = true
          const filterBody = {
            ...model,
            column: this.columns.filter((item) => item != 'address'),
            type: 'temperature',
            ...filter,
          }
          const res = await this.queryCache.fetch(
            GET_REPORT_RAW_SENSOR_PAGINATED,
            {
              filter: filterBody,
            },
            self.reportKey
          )
          const result = res.rawSensorInfoPaginated.rawSensorInfoPaginated
          self.totalRows = result?.pagination?.total ?? 0
          self.isBusy = false
          self.message = res.rawSensorInfoPaginated.message
          self.isError = !res.rawSensorInfoPaginated.isSuccessful
          self.modalVerifReport += 1
          self.actualTotal = result?.pagination?.actualTotal ?? 0
          return result
        },
        getGraph: async () => {
          const { valid, model } = this.generateFilter
          const self = this
          const filterBody = {
            ...model,
            column: this.columns.filter((item) => item !== 'address' && item !== 'geofence'),
            type: 'temperature',
          }
          if (
            this.getFeatureFlagData('vsms2_report_temperature_version') &&
            this.checkApiVersion(filterBody, this.report.reportKey ?? this.report.key)
          ) {
            filterBody.version = this.getFeatureFlagData('vsms2_report_temperature_version').value
          }
          if (!valid || !self.isShowGraph) return {}
          const res = await this.$apollo.query({
            query: GET_REPORT_RAW_SENSOR,
            variables: {
              filter: filterBody,
            },
            fetchPolicy: 'no-cache',
          })
          self.chartData.datasets = []

          let data = res.data

          self.chartData.labels = data.rawSensorInfos.rawSensorInfos.map(
            (item) => `${self.momentDMY_HI(item.createdOn)} ${self.getLabelTimezone()}`
          )

          let temp1 = self.fields.findIndex((item) => {
            return item.key == 'temperature'
          })

          if (temp1 >= 0) {
            self.chartData.datasets.push({
              label: 'Suhu 1',
              data: data.rawSensorInfos.rawSensorInfos.map((item) => item.temperature),
              borderColor: '#000096',
              fill: false,
              pointRadius: 0,
              lineTension: 0,
              borderWidth: 2,
              spanGaps: true,
            })
          }

          let temp2 = self.fields.findIndex((item) => {
            return item.key == 'temperature2'
          })

          if (temp2 >= 0) {
            self.chartData.datasets.push({
              label: 'Suhu 2',
              data: data.rawSensorInfos.rawSensorInfos.map((item) => item.temperature2),
              borderColor: '#FF8A00',
              fill: false,
              pointRadius: 0,
              lineTension: 0,
              borderWidth: 2,
              spanGaps: true,
            })
          }

          let temp3 = self.fields.findIndex((item) => {
            return item.key == 'temperature3'
          })

          if (temp3 >= 0) {
            self.chartData.datasets.push({
              label: 'Suhu 3',
              data: data.rawSensorInfos.rawSensorInfos.map((item) => item.temperature3),
              borderColor: '#06FFFF',
              fill: false,
              pointRadius: 0,
              lineTension: 0,
              borderWidth: 2,
              spanGaps: true,
            })
          }

          let acc = self.fields.findIndex((item) => {
            return item.key == 'acc'
          })

          if (acc >= 0) {
            self.chartData.datasets.push({
              yAxisID: 'pintu',
              label: 'ACC',
              data: data.rawSensorInfos.rawSensorInfos.map(function (item) {
                if (item.acc) {
                  return item.acc == 'on' ? 3 : 2
                } else {
                  return null
                }
              }),
              borderColor: '#F41D00',
              fill: false,
              pointRadius: 0,
              lineTension: 0,
              borderWidth: 2,
              steppedLine: true,
            })
          }

          let refrigerator = self.fields.findIndex((item) => {
            return item.key == 'refrigerator'
          })

          if (refrigerator >= 0) {
            self.chartData.datasets.push({
              yAxisID: 'pintu',
              label: 'Refrigerator',
              data: data.rawSensorInfos.rawSensorInfos.map(function (item) {
                if (item.refrigerator) {
                  return item.refrigerator == 'on' ? 5 : 4
                } else {
                  return null
                }
              }),
              borderColor: '#D9D93F',
              fill: false,
              pointRadius: 0,
              lineTension: 0,
              borderWidth: 2,
              steppedLine: true,
            })
          }

          let indexDoor1 = self.fields.findIndex((item) => {
            return item.key == 'door1'
          })

          if (indexDoor1 >= 0) {
            self.chartData.datasets.push({
              yAxisID: 'pintu',
              label: 'Pintu 1',
              data: data.rawSensorInfos.rawSensorInfos.map((item) =>
                item.door1 == 1 ? 7 : item.door1 !== null ? 6 : null
              ),
              borderColor: '#00F00F',
              fill: false,
              pointRadius: 0,
              lineTension: 0,
              borderWidth: 2,
              steppedLine: true,
            })
          }

          let indexDoor2 = self.fields.findIndex((item) => {
            return item.key == 'door2'
          })

          if (indexDoor2 >= 0) {
            self.chartData.datasets.push({
              yAxisID: 'pintu',
              label: 'Pintu 2',
              data: data.rawSensorInfos.rawSensorInfos.map((item) =>
                item.door2 == 1 ? 7 : item.door2 !== null ? 6 : null
              ),
              borderColor: '#0085FF',
              fill: false,
              pointRadius: 0,
              lineTension: 0,
              borderWidth: 2,
              steppedLine: true,
            })
          }

          if (data.rawSensorInfos.rawSensorInfos.length === 0) {
            self.isLoadingGraph = false
            const dates = this.getDatesInRange(this.filters.periode[0], this.filters.periode[1])
            dates.forEach(function (date) {
              self.chartData.labels.push(`${self.momentDMHms(date)} ${self.getLabelTimezone()}`)
            })
          }
          self.isLoadingGraph = false
          const result = {
            chartData: self.chartData,
            yAxesAdditional: self.yAxesAdditional,
            isLoadingGraph: self.isLoadingGraph,
          }
          return result
        },
      }
    },
    periodeLabel() {
      return this.filters.periode
        ? this.momentDatetimeShortMonth(this.filters.periode[0]) +
            ' - ' +
            this.momentDatetimeShortMonth(this.filters.periode[1])
        : '-'
    },
    licensePlateLabel() {
      if (!Array.isArray(this.filters.vehicleId) && this.filters.vehicleId) {
        return this.filterDetail != {} ? this.filterDetail.licensePlate : '-'
      } else if (
        Array.isArray(this.filters.vehicleId) &&
        this.filters.vehicleId &&
        this.filters.vehicleId.length > 0
      ) {
        return this.filterDetail != {} ? this.filterDetail.licensePlate : '-'
      } else {
        return '-'
      }
    },
    vehicleIdCount() {
      return this.filters.vehicleId &&
        Array.isArray(this.filters.vehicleId) &&
        this.filters.vehicleId.length > 0
        ? this.filters.vehicleId.length
        : 0
    },
    savedReport() {
      return JSON.parse(localStorage.getItem('saveReports'))
    },
    report() {
      const reports = [...reportList].concat(this.savedReport)
      return reports.find((item) => item.key == this.reportKey)
    },
    filterValue() {
      return this.$store.state.report.filterValue
    },
    hasCsvScope() {
      return this.report.downloadOptions?.includes('csv') && this.hasAccess(['csv_report:read'])
    },
    itemDownloadDropdown() {
      let list = []
      if (this.hasOnlyOneVehicleReport(this.filters.vehicleId)) {
        list = list.concat([
          {
            name: 'excel',
            label: this.$t('global.action.downloadExcel'),
            onClick: () => {
              this.downloadReport('xlsx')
            },
            icon: this.excelIcon,
          },
          {
            name: 'pdf',
            label: this.$t('global.action.downloadPdf'),
            onClick: () => {
              this.downloadReport('pdf')
            },
            icon: this.pdfIcon,
          },
        ])
      }
      if (this.hasCsvScope) {
        list.push({
          name: 'csv',
          label: this.$t('global.action.downloadCsv'),
          onClick: () => {
            this.downloadReport('csv')
          },
          icon: this.excelIcon,
        })
      }
      if ((this.isShowGraph ?? true) && this.chartData.datasets.length > 1) {
        list.push({
          name: 'graph',
          label: this.$t('global.action.downloadGraphicPdf'),
          onClick: () => {
            this.downloadReport('graph')
          },
          icon: this.pdfIcon,
        })
      }
      return list
    },
    hasEmail() {
      return this.emails && this.emails.length > 0
    },
    getActiveDataSet() {
      return {
        labels: () => {
          const dataSet = this.chartData.datasets.map((item) => {
            return item._meta
              ? Object.keys(item._meta)
                ? { hidden: item._meta[Object.keys(item._meta)[0]]?.hidden ?? false }
                : { hidden: false }
              : { hidden: false }
          })
          const finalDataSet = dataSet.map((item) => item.hidden ?? false)
          const getDataSet = []
          finalDataSet.forEach((item, index) => {
            if (!item) {
              switch (index) {
                case 0:
                  getDataSet.push('temperature')
                  break
                case 1:
                  getDataSet.push('temperature2')
                  break
                case 2:
                  getDataSet.push('temperature3')
                  break
                case 3:
                  getDataSet.push('acc')
                  break
                case 4:
                  getDataSet.push('refrigerator')
                  break
                case 5:
                  getDataSet.push('door1')
                  break
                case 6:
                  getDataSet.push('door2')
                  break
              }
            }
          })
          return getDataSet
        },
      }
    },
  },
  methods: {
    getBaseFilter(filters) {
      if (!filters || Object.keys(filters).length <= 0) return {}
      const result = {}
      Object.keys(filters).forEach((item) => {
        if (
          filters[item] !== null &&
          ((Array.isArray(filters[item]) && filters[item].length > 0) ||
            (!Array.isArray(filters[item]) && filters[item]))
        ) {
          result[item] = filters[item]
        }
      })
      const { periode, interval, ...otherFilter } = result
      const startDate = periode.length > 0 ? moment(periode[0]).toISOString() : null
      const endDate = periode.length > 0 ? moment(periode[1]).toISOString() : null
      let intervalData = interval
      if (!this.hasAccess(['interval_report:read'])) {
        intervalData = undefined
      }
      const model = {
        startDate,
        endDate,
        interval: intervalData,
        ...otherFilter,
      }
      return model
    },
    handleTableChanged(isTable) {
      if (!isTable) {
        this.track(`View ${this.report.event} Chart`)
      } else {
        this.track('View ' + this.report.event)
        if (!this.mediaQuery.matches) {
          this.$store.dispatch('layout/toggleMenu', true)
        }
      }
    },
    handleColumnReport(fields) {
      this.reportFields = fields
      this.tableKey += 1
      this.summaryKey += 1
    },
    forceRerender() {
      this.filters = { ...this.$store.state.report.filterReport }
      this.filterDetail = this.getReportDetail(this.reportKey) ?? {}
      this.tableKey += 1
      this.summaryKey += 1
    },
    showModal() {
      this.$refs['modal-filter'].SetFormValues(this.filterValue)
      this.$bvModal.show(this.report.modal)
    },
    redirectToMap(lat, long) {
      let url = `https://www.google.com/maps/search/?api=1&query=${lat},${long}`
      window.open(url, '_blank')
    },
    handleEmails(emails) {
      this.emails = emails
      this.recipientsEmail = emails
      this.isSubmitEmail = true
      if (emails.length > 0) {
        localStorage.setItem('emailReceiverReport', emails.join())
      }
      this.$bvModal.hide(this.idEmailModal)
      this.$mcToast.info(this.$t('report.text.popupEmailInfo'))
      this.downloadReport(this.reportAfterEmail)
    },
    showDownloader(periode, extension, result) {
      const downloadProgress = {
        status: 'I',
        url: null,
        name:
          `${extension === 'graph' ? 'Grafik ' : ''}${this.title} ` +
          moment(periode[0]).format('DD MMM YYYY') +
          ' - ' +
          moment(periode[1]).format('DD MMM YYYY') +
          `.${extension === 'graph' ? 'pdf' : extension}`,
        periode:
          moment(periode[0]).format('DD MMM YYYY') +
          ' - ' +
          moment(periode[1]).format('DD MMM YYYY'),
        downloadedAt: moment().format('DD MMM YYYY HH:mm:ss'),
        type: extension === 'graph' ? 'pdf' : extension,
        id: result.rawSensorInfoReport.id,
      }
      if (result.isSuccessful) {
        this.$store.commit('report/doneProgress', downloadProgress)
      } else {
        downloadProgress.status = 'F'
        this.$store.commit('report/failProgress', downloadProgress)
      }
      this.$store.commit('report/showDownloadPopup', true)
    },
    downloadReport(extension, contact) {
      if (
        !this.isSubmitEmail &&
        extension !== 'graph' &&
        this.actualTotal > this.totalRows &&
        (!contact || contact.length <= 0)
      ) {
        this.$bvModal.show(this.idEmailModal)
        this.reportAfterEmail = extension
        return
      }
      this.isSubmitEmail = false

      const fileType = this.trackDownloadReport(this.report, extension, contact)

      const model = this.filterReport
      if (!model) {
        setTimeout(() => {
          this.showModal()
        }, 1000)
        return false
      }
      const { periode } = this.filters
      let filterVar = {
        ...model,
        subtitle: `Periode : ${this.momentDateLongMonth(periode[0])} - ${this.momentDateLongMonth(
          periode[1]
        )}`,
        downloadAs: fileType,
        emails: this.emails,
      }
      if (extension === 'graph') {
        filterVar = {
          ...filterVar,
          column: this.allColumn.filter((field) => field != 'address' && field !== 'geofence'),
          labels: this.getActiveDataSet.labels(),
        }
      }

      if (this.report.labelName) {
        filterVar.title = this.report.labelName
      }

      if (contact) {
        filterVar = {
          ...filterVar,
          isShared: true,
          contact: contact,
        }
      }

      this.$apollo
        .query({
          query: RAW_SENSOR_INFO_REPORT,
          variables: {
            filter: filterVar,
          },
          fetchPolicy: 'no-cache',
        })
        .then((res) => {
          let isSuccessful = res.data.rawSensorInfoReport.isSuccessful
          if (!this.hasEmail && !contact) {
            this.showDownloader(periode, extension, res.data.rawSensorInfoReport)
          } else if (contact) {
            if (isSuccessful) this.$mcToast.success(this.$t('report.text.sharedSuccess'))
            else this.$mcToast.error(this.$t('report.text.sharedFailed'))
          }
        })
    },
  },
}
</script>
